import {getFieldBySlug} from '@packages/systems/dynamo/utils/CollectionUtils';
import {isImageRefSet} from '@packages/systems/dynamo/utils/CollectionFieldUtils';

import {
  DEFAULT_SKU_SLUG,
  PRODUCTS_BINDING_CONTEXT_EXTERNAL_KEY,
} from '@packages/systems/commerce/constants';
import {
  getSkuCollection,
  getProductCollection,
  getCategoriesCollection,
  isProductsCollection,
  isCategoriesCollection,
} from '@packages/systems/commerce/utils/CommerceCollectionUtils';

import {defineCollectionTypeReference} from '@packages/systems/cms/gateway/shared';

import {MORE_IMAGES_BINDING_CONTEXT_TYPE_ID} from './identifiers';
import {cartType as cartTypeDef} from './cart';
import {orderItemType as orderItemTypeDef} from './order-items';
import {productOptionType as productOptionTypeDef} from './product-options';
import {addToCartType as addToCartTypeDef} from './addToCart';
import {checkoutPageRootContextType as checkoutPageRootContextTypeDef} from './checkout-page';
import {paypalCheckoutPageRootContextType as paypalCheckoutPageRootContextTypeDef} from './checkout-paypal-page';
import {orderConfirmationPageRootContextType as orderConfirmationPageRootContextTypeDef} from './order-confirmation-page';
import {
  orderType as orderTypeDef,
  checkoutType as checkoutTypeDef,
  paypalCheckoutType as paypalCheckoutTypeDef,
} from './order';
import {shippingMethodType as shippingMethodTypeDef} from './shipping-methods';
import {extraItemType as extraItemTypeDef} from './extra-items';
import {downloadType} from './downloads';

import type {BindingContextTypeDef} from '@packages/systems/dynamo/source-types';
import type {
  Collections,
  CollectionRecordType as Collection,
} from '@packages/systems/dynamo/records/CollectionRecord';
import type {FieldType as Field} from '@packages/systems/dynamo/records/Field';

export default function defineBindingContextTypes(
  collections: Collections
): BindingContextTypeDef[] {
  const products = getProductCollection(collections);
  const categories = getCategoriesCollection(collections);
  const skus = getSkuCollection(collections);

  const rootTypeDef = {
    entity: 'Root',
    label: 'Ecommerce',
  } as const;

  const typeDefs = [
    rootTypeDef,
    cartTypeDef,
    orderItemTypeDef,
    productOptionTypeDef,
    checkoutPageRootContextTypeDef,
    paypalCheckoutPageRootContextTypeDef,
    orderConfirmationPageRootContextTypeDef,
    orderTypeDef,
    checkoutTypeDef,
    paypalCheckoutTypeDef,
    shippingMethodTypeDef,
    extraItemTypeDef,
    downloadType,
  ];

  if (products) {
    const productsTypeReference = defineCollectionTypeReference(products);

    const productsTypeDef = {
      entity: productsTypeReference.entity,
      id: productsTypeReference.id,
      label: products.name,
      pluralLabel: products.pluralName,
      singularLabel: products.singularName,
      iconName: determineIconName(products),
      externalKey: PRODUCTS_BINDING_CONTEXT_EXTERNAL_KEY,
    } as const;

    typeDefs.push(
      productsTypeDef,
      ...defineMultiImageFieldTypes(products),
      addToCartTypeDef
    );
  }

  if (categories) {
    const categoriesTypeReference = defineCollectionTypeReference(categories);

    const categoriesTypeDef = {
      entity: categoriesTypeReference.entity,
      id: categoriesTypeReference.id,
      label: categories.name,
      pluralLabel: categories.pluralName,
      singularLabel: categories.singularName,
      iconName: determineIconName(categories),
    } as const;

    typeDefs.push(categoriesTypeDef, ...defineMultiImageFieldTypes(categories));
  }

  if (skus) {
    const skusTypeReference = defineCollectionTypeReference(skus);
    /*
      This is a placeholder binding context type for the SKUs collection.

      This binding context type is a placeholder to ensure
      any SKU type references have a corresponding binding context type, but
      it shouldn't have any binding sources or relative binding contexts.

      Binding context types that have a SKU reference should handle expanding
      the reference fields to binding sources internally.
    */
    const placeholder_skusTypeDef = {
      entity: skusTypeReference.entity,
      id: skusTypeReference.id,
      label: skus.name,
      pluralLabel: skus.pluralName,
      singularLabel: skus.singularName,
    } as const;
    typeDefs.push(placeholder_skusTypeDef);
  }

  const moreImagesField = skus ? getFieldBySlug(skus, 'more-images') : null;
  const defaultSkuField = products
    ? getFieldBySlug(products, DEFAULT_SKU_SLUG)
    : null;

  if (products && defaultSkuField && moreImagesField) {
    const moreImagesTypeDef = {
      entity: 'Field',
      id: MORE_IMAGES_BINDING_CONTEXT_TYPE_ID,
      label: moreImagesField.name,
      iconName: determineIconName(products),
    } as const;
    typeDefs.push(moreImagesTypeDef);
  }

  return typeDefs;
}

/*
  Create BindingContextTypes for each multi-image field of a collection
*/
export function defineMultiImageFieldTypes(
  collection: Collection
): BindingContextTypeDef[] {
  return collection.fields.reduce<Array<any>>((definitions, field) => {
    if (isImageRefSet(field)) {
      definitions.push(
        defineMultiImageFieldBindingContextType(field, collection)
      );
    }
    return definitions;
  }, []);
}

function defineMultiImageFieldBindingContextType(
  field: Field,
  collection: Collection
) {
  // TODO: Address field id uniqueness
  return {
    entity: 'Field',
    id: field.id,
    label: field.name,
    iconName: determineIconName(collection),
  };
}

function determineIconName(collection: Collection) {
  if (isProductsCollection(collection)) {
    return 'product';
  }
  if (isCategoriesCollection(collection)) {
    return 'category';
  }
  return 'collection';
}
